import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import {
  getTagsRequest,
  createTagRequest,
  deleteTagRequest,
  getTagDetailsRequest,
  updateTagRequest,
} from "services/modules/lookups/tags";
import TagTemplate from "templates/lookups/tag";
import { toast } from "react-toastify";
import dayjs from "dayjs";

function TagPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.tags.tags);
  const count = useSelector((state) => state.tags.count);
  const details = useSelector((state) => state.tags.tag);
  const load = useSelector((state) => state.tags.load);

  //permissions
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.tags
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getTagDetailsRequest({ tag_id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getTagsRequest(requestData));
  };

  // mappedData use for create custom cell to concat multi values or navigate button
  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      created_at: <>{dayjs(details?.created_at).format("DD-MM-YYYY hh:mm")}</>,
      updated_at: <>{dayjs(details?.updated_at).format("DD-MM-YYYY hh:mm")}</>,
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/tags",
      title: t("lookups_tags.title"),
    },
    type && {
      active: false,
      title: type === "details" ? t("lookups_tags.details") : id ? t("lookups_tags.edit") : t("lookups_tags.add"),
    },
  ];
  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/tags");
      !id && formik?.resetForm();
      // USE TOSTIFY TO CONFIRM ACTION
      toast.success(id ? t("lookups_tags.updated") : t("lookups_tags.created"));
    };
    const requestData = {
      body: {
        tag: {
          image: data?.image,
          en: {
            name: data?.englishTagName,
          },
          ar: {
            name: data?.arabicTagName,
          },
        },
      },
      action,
    };

    if (id) {
      //update call
      dispatch(updateTagRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(createTagRequest(requestData));
    }
  };

  const filterList = [];

  const handleDelete = (id) => {
    //action to be called after successfull deletion
    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj((prev) => ({
          ...prev,
          page_number: page - 1,
        }));
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/tags");
      toast.success(t("lookups_tags.deleted"));
    };
    const requestData = {
      id,
      action, //dispatched on success
    };
    dispatch(deleteTagRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  //listing all tags
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  //details of a single tag
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  const token = localStorage.getItem("token");
  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <TagTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default TagPage;
